/**
 * Canonical typography scale. Pure value (no React/RN imports).
 *
 * Single source for all typography scales:
 *  - TYPOGRAPHY        primary app-wide scale (value-identical to legacy REDESIGN_TYPOGRAPHY)
 *  - PAYMENT_TYPOGRAPHY payment-feature scale (relocated verbatim)
 *  - MOBILE_TYPOGRAPHY  mobile-screen scale (relocated verbatim)
 *
 * The three legacy names (REDESIGN_TYPOGRAPHY / PAYMENT_TYPOGRAPHY /
 * MOBILE_TYPOGRAPHY) are @deprecated re-exports of these — prefer useTypography().
 */
const FONT_FAMILY =
  'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

export const TYPOGRAPHY = {
  heading1: { fontFamily: FONT_FAMILY, fontSize: 28, fontWeight: '700', lineHeight: 32.2, letterSpacing: -0.5 },
  // One step under heading1 for surfaces that can't spend 28pt on a title —
  // the phone's no-scroll home greeting. Same face and weight; line-height at
  // heading1's 1.15 ratio.
  heading2: { fontFamily: FONT_FAMILY, fontSize: 22, fontWeight: '700', lineHeight: 25.3, letterSpacing: -0.4 },
  sectionTitle: { fontFamily: FONT_FAMILY, fontSize: 17, fontWeight: '600' },
  cardTitle: { fontFamily: FONT_FAMILY, fontSize: 14, fontWeight: '600' },
  cardTitleLarge: { fontFamily: FONT_FAMILY, fontSize: 16, fontWeight: '600', lineHeight: 20.8 },
  body: { fontFamily: FONT_FAMILY, fontSize: 14, fontWeight: '400' },
  small: { fontFamily: FONT_FAMILY, fontSize: 13, fontWeight: '500' },
  caption: { fontFamily: FONT_FAMILY, fontSize: 12, fontWeight: '400' },
  captionMedium: { fontFamily: FONT_FAMILY, fontSize: 12, fontWeight: '500' },
  label: { fontFamily: FONT_FAMILY, fontSize: 11, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase' },
  amount: { fontFamily: FONT_FAMILY, fontSize: 36, fontWeight: '700', letterSpacing: -1 },
  balanceLabel: { fontFamily: FONT_FAMILY, fontSize: 13, fontWeight: '500', letterSpacing: 1, textTransform: 'uppercase' },
  button: { fontFamily: FONT_FAMILY, fontSize: 14, fontWeight: '600' },
  postTitle: { fontFamily: FONT_FAMILY, fontSize: 15, fontWeight: '600', lineHeight: 20.25, letterSpacing: -0.2 },
  postBody: { fontFamily: FONT_FAMILY, fontSize: 13, fontWeight: '400', lineHeight: 18.85 },
  authorName: { fontFamily: FONT_FAMILY, fontSize: 13, fontWeight: '600' },
  timestamp: { fontFamily: FONT_FAMILY, fontSize: 11, fontWeight: '400' },
  statText: { fontFamily: FONT_FAMILY, fontSize: 12, fontWeight: '500' },
  tagFilter: { fontFamily: FONT_FAMILY, fontSize: 12, fontWeight: '600' },
  listingPrice: { fontFamily: FONT_FAMILY, fontSize: 15, fontWeight: '700' },
  listingTitle: { fontFamily: FONT_FAMILY, fontSize: 13, fontWeight: '500' },
  conditionBadge: { fontFamily: FONT_FAMILY, fontSize: 11, fontWeight: '500' },
  buttonSmall: { fontFamily: FONT_FAMILY, fontSize: 13, fontWeight: '600' },
} as const;

/** Payment-feature type scale (Figma). Relocated verbatim from paymentDesignTokens. */
export const PAYMENT_TYPOGRAPHY = {
  heading2xl: { fontSize: 24, fontWeight: '700', lineHeight: 36 },
  headingLg: { fontSize: 18, fontWeight: '600', lineHeight: 27 },
  headingMd: { fontSize: 16, fontWeight: '600', lineHeight: 24 },
  bodySm: { fontSize: 14, fontWeight: '400', lineHeight: 21 },
  bodySmMedium: { fontSize: 14, fontWeight: '500', lineHeight: 21 },
  bodySmSemiBold: { fontSize: 14, fontWeight: '600', lineHeight: 21 },
  textXs: { fontSize: 12, fontWeight: '400', lineHeight: 18 },
  textXsMedium: { fontSize: 12, fontWeight: '500', lineHeight: 18 },
  textXsLight: { fontSize: 12, fontWeight: '300', lineHeight: 18 },
} as const;

/** Mobile-screen type scale (Figma). Relocated verbatim from mobileTypography. */
export const MOBILE_TYPOGRAPHY = {
  title: { fontSize: 18, fontWeight: '600', lineHeight: 27 },
  subtitle: { fontSize: 14, fontWeight: '400', lineHeight: 21 },
  body: { fontSize: 14, fontWeight: '400', lineHeight: 21 },
  bodyMedium: { fontSize: 14, fontWeight: '500', lineHeight: 21 },
  bodyLarge: { fontSize: 16, fontWeight: '400', lineHeight: 24 },
  label: { fontSize: 12, fontWeight: '500', lineHeight: 18 },
  link: { fontSize: 12, fontWeight: '500', lineHeight: 18 },
  caption: { fontSize: 12, fontWeight: '400', lineHeight: 18 },
} as const;
